import { forwardRef, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";

const Select = forwardRef(({
  label,
  error,
  children,
  className,
  required,
  options,
  placeholder,
  value,
  onChange,
  name,
  searchable = false,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const portalMenu = document.getElementById(`select-portal-${name}`);
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        (!portalMenu || !portalMenu.contains(e.target))
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, name, updatePosition]);

  // Custom dropdown mode when options array is passed
  if (options) {
    const selectedOption = options.find(o => o.value === value);

    const filteredOptions = searchable && search.trim()
      ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
      : options;

    const handleSelect = (optValue) => {
      if (onChange) {
        onChange({ target: { name, value: optValue } });
      }
      setIsOpen(false);
      setSearch("");
    };

    const handleOpen = () => {
      updatePosition();
      setIsOpen(prev => !prev);
      if (isOpen) setSearch("");
    };

const dropdownMenu = isOpen && createPortal(
      <div
        id={`select-portal-${name}`}
        style={{
          position: "fixed",
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          zIndex: 99999,
          background: "#1A1A2E",
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 16px 60px rgba(0,0,0,0.7), 0 4px 20px rgba(0,0,0,0.5)",
          overflow: "hidden",
          maxHeight: "320px",
          display: "flex",
          flexDirection: "column",
        }}
role="listbox"
      >
        {searchable && (
          <div style={{ padding: "8px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca cliente..."
              autoFocus
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px",
                padding: "6px 10px",
                color: "#ffffff",
                fontSize: "0.875rem",
                outline: "none",
                boxSizing: "border-box",
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
        )}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: "12px 16px", color: "rgba(255,255,255,0.4)", fontSize: "0.875rem" }}>
              Nessun risultato
            </div>
          ) : (
            filteredOptions.map((option, idx) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 16px",
                    textAlign: "left",
                    color: "#ffffff",
                    background: isSelected ? "rgba(124,58,237,0.30)" : "transparent",
                    borderTop: idx !== 0 ? "1px solid rgba(255,255,255,0.08)" : "none",
                    border: "none",
                    borderRadius: 0,
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: isSelected ? "600" : "500",
                    transition: "background 0.15s",
                    boxShadow: "none",
                    backdropFilter: "none",
                    transform: "none",
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(124,58,237,0.20)"; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  {option.icon && (
                    <span style={{ fontSize: "1.125rem", lineHeight: 1, flexShrink: 0 }}>{option.icon}</span>
                  )}
                  <span>{option.label}</span>
                  {isSelected && (
                    <svg style={{ width: 16, height: 16, marginLeft: "auto", color: "#c4b5fd", flexShrink: 0 }} fill="none" viewBox="0 0 20 20">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m4 10 4.5 4.5 7.5-9" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>,
      document.body
    );

    return (
      <div className="space-y-2" ref={containerRef}>
        {label && (
          <label className="block text-body-sm font-medium text-gray-300">
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            onClick={handleOpen}
            className={cn(
              "w-full px-3 py-2.5 text-left flex items-center justify-between gap-2",
              "bg-white/10 border border-white/[0.15] rounded-xl",
              "shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60",
              "hover:bg-white/[0.14] hover:border-white/[0.25]",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              error && "border-red-400/70 focus:border-red-400 focus:ring-red-400/40",
              isOpen && "border-primary/70 ring-2 ring-primary/30 bg-white/[0.12] shadow-[0_0_0_3px_rgba(124,58,237,0.18),inset_0_1px_0_rgba(255,255,255,0.10)]",
              className
            )}
            aria-invalid={error ? "true" : "false"}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          >
            <span className={cn("flex items-center gap-2 truncate", !selectedOption ? "text-white/60" : "text-white font-medium")}>
              {selectedOption ? (
                <>
                  {selectedOption.icon && <span className="text-base leading-none">{selectedOption.icon}</span>}
                  <span>{selectedOption.label}</span>
                </>
              ) : (
                <span>{placeholder || "Seleziona un'opzione"}</span>
              )}
            </span>
            <svg
              className={cn("w-4 h-4 text-white/80 shrink-0 transition-transform duration-200", isOpen && "rotate-180")}
              fill="none"
              viewBox="0 0 20 20"
            >
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4" />
            </svg>
          </button>

          {dropdownMenu}
        </div>

        {error && (
          <p
            id={`${name}-error`}
            className="text-body-sm text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  // Native select fallback (backwards compatible)
  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-body-sm font-medium text-gray-300">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      <select
        ref={ref}
        name={name}
        value={value}
        onChange={onChange}
        className={cn(
          "w-full px-3 py-2.5 bg-white/5 border border-primary/30 rounded-input text-white transition-colors duration-normal appearance-none bg-no-repeat bg-right pr-10",
          "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          error && "border-red-400 focus:border-red-400 focus:ring-red-400/50",
          className
        )}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 0.5rem center',
          backgroundSize: '1.5em 1.5em'
        }}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${name}-error` : undefined}
        {...props}
      >
        {children}
      </select>

      {error && (
        <p
          id={`${name}-error`}
          className="text-body-sm text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Select.displayName = "Select";

export default Select;